import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { apiFetch } from '@/utils/api';
import PaginationFooter from '@/components/PaginationFooter';
import { Search } from 'lucide-react';
import { TagBadge, Tag } from '@/components/TagBadge';
import CategoryDropdown from '@/components/CategoryDropdown';
import { useI18n } from '@/hooks/useI18n';

interface CostCategory {
    id: string;
    name: string;
}

interface RevenueCategory {
    id: string;
    name: string;
}

interface VendorBankAccount {
    id: string;
    vendor_id: string;
    account_number?: string;
    iban?: string;
    domestic_account_number?: string;
    bank_name?: string;
    is_primary: boolean;
    is_active: boolean;
}

interface Vendor {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    tax_id?: string;
    cost_category?: CostCategory;
    revenue_category?: RevenueCategory;
    bank_accounts?: VendorBankAccount[];
    auto_create_invoices: boolean;
    is_active: boolean;
}

interface Pagination {
    page: number;
    total: number;
    pages: number;
    limit: number;
}

export default function VendorsPage() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [tagsByVendorId, setTagsByVendorId] = useState<Record<string, Tag[]>>({});
    const [selectedVendorIds, setSelectedVendorIds] = useState<Set<string>>(new Set());
    const [bulkCostCategoryId, setBulkCostCategoryId] = useState<string | null | undefined>(undefined);
    const [bulkUpdating, setBulkUpdating] = useState<boolean>(false);
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        total: 0,
        pages: 0,
        limit: 20,
    });

    useEffect(() => {
        fetchVendors();
    }, [pagination.page, pagination.limit, searchQuery]);

    const fetchVendors = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                limit: pagination.limit.toString(),
            });

            if (searchQuery) {
                params.append('search', searchQuery);
            }

            const response = await apiFetch(`/api/vendors/?${params}`);
            if (response.ok) {
                const data = await response.json();
                const vendorsList = data.vendors || data;
                setVendors(vendorsList);
                setSelectedVendorIds((prev) => {
                    const visibleVendorIds = new Set(vendorsList.map((vendor: Vendor) => vendor.id));
                    return new Set(Array.from(prev).filter((vendorId) => visibleVendorIds.has(vendorId)));
                });
                if (data.pagination) {
                    setPagination(prev => ({ ...prev, ...data.pagination }));
                }

                // Fetch tags for all vendors in bulk
                if (vendorsList.length > 0) {
                    fetchVendorTagsBulk(vendorsList.map((v: Vendor) => v.id));
                }
            }
        } catch (error) {
            console.error('Error fetching vendors:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchVendorTagsBulk = async (vendorIds: string[]) => {
        try {
            const response = await apiFetch('/api/tags/entities/vendors/bulk', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(vendorIds),
            });
            if (response.ok) {
                const tagsData = await response.json();
                // Convert the response to a map of vendor_id -> tags
                const tagsMap: Record<string, Tag[]> = {};
                for (const [vendorId, entityTags] of Object.entries(tagsData)) {
                    tagsMap[vendorId] = (entityTags as any[]).map((et: any) => et.tag);
                }
                setTagsByVendorId(tagsMap);
            }
        } catch (error) {
            console.error('Error fetching vendor tags in bulk:', error);
        }
    };

    const handleRowClick = (vendorId: string) => {
        navigate({ to: `/partners/${vendorId}` });
    };

    const handleToggleVendorSelection = (vendorId: string, checked: boolean) => {
        setSelectedVendorIds((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(vendorId);
            } else {
                next.delete(vendorId);
            }
            return next;
        });
    };

    const handleSelectAllVisible = (checked: boolean) => {
        if (checked) {
            setSelectedVendorIds(new Set(vendors.map((vendor) => vendor.id)));
            return;
        }
        setSelectedVendorIds(new Set());
    };

    const handleBulkCostCategoryUpdate = async () => {
        const vendorIds = Array.from(selectedVendorIds);
        if (vendorIds.length === 0 || bulkCostCategoryId === undefined) {
            return;
        }

        setBulkUpdating(true);
        try {
            const results = await Promise.all(
                vendorIds.map(async (vendorId) => {
                    const response = await apiFetch(`/api/vendors/${vendorId}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ cost_category_id: bulkCostCategoryId }),
                    });

                    return { vendorId, ok: response.ok };
                }),
            );

            const failed = results.filter((result) => !result.ok);
            if (failed.length === 0) {
                alert(t('vendors.bulkUpdateSuccess', 'finance', { count: vendorIds.length.toString() }));
            } else if (failed.length === vendorIds.length) {
                alert(t('vendors.bulkUpdateFailed', 'finance'));
            } else {
                alert(
                    t('vendors.bulkUpdatePartial', 'finance', {
                        total: vendorIds.length.toString(),
                        failed: failed.length.toString(),
                    }),
                );
            }

            setBulkCostCategoryId(undefined);
            setSelectedVendorIds(new Set());
            await fetchVendors();
        } catch (error) {
            console.error('Error bulk updating vendor cost categories:', error);
            alert(t('vendors.bulkUpdateFailed', 'finance'));
        } finally {
            setBulkUpdating(false);
        }
    };

    const allVisibleSelected = vendors.length > 0 && selectedVendorIds.size === vendors.length;

    return (
        <Layout>
            <div className="space-y-6">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">{t('navigation.partners', 'common')}</h1>
                    <p className="mt-1 text-muted-foreground">
                        {t('vendors.subtitle', 'finance')}
                    </p>
                </div>

                {/* Search Bar */}
                <div className="flex gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder={t('vendors.searchPlaceholder', 'finance')}
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setPagination(prev => ({ ...prev, page: 1 }));
                            }}
                            className="pl-10"
                        />
                    </div>
                </div>

                {selectedVendorIds.size > 0 && (
                    <div className="flex flex-col gap-3 rounded-lg border p-4">
                        <div className="text-sm font-medium">
                            {t('vendors.bulkSelectedCount', 'finance', { count: selectedVendorIds.size.toString() })}
                        </div>
                        <div className="flex flex-col gap-3 md:flex-row md:items-center">
                            <div className="w-full md:max-w-md">
                                <CategoryDropdown
                                    selectedCategoryId={bulkCostCategoryId}
                                    onCategoryChange={setBulkCostCategoryId}
                                    placeholder={t('vendors.bulkCategoryPlaceholder', 'finance')}
                                    allOptionLabel={t('vendors.bulkClearCategory', 'finance')}
                                />
                            </div>
                            <Button
                                onClick={handleBulkCostCategoryUpdate}
                                disabled={bulkUpdating || bulkCostCategoryId === undefined}
                            >
                                {bulkUpdating
                                    ? t('vendors.bulkApplying', 'finance')
                                    : t('vendors.bulkApplyCategory', 'finance')}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Vendors Table */}
                <div className="">
                    <div className="min-h-[600px] relative">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-10">
                                        <Checkbox
                                            checked={allVisibleSelected}
                                            onCheckedChange={(checked) => handleSelectAllVisible(Boolean(checked))}
                                        />
                                    </TableHead>
                                    <TableHead>{t('common.name', 'common')}</TableHead>
                                    <TableHead>{t('tags.tags', 'common')}</TableHead>
                                    <TableHead>{t('vendors.taxNumber', 'finance')}</TableHead>
                                    <TableHead>{t('vendors.address', 'finance')}</TableHead>
                                    <TableHead>{t('vendors.contact', 'finance')}</TableHead>
                                    <TableHead>{t('vendors.costCategory', 'finance')}</TableHead>
                                    <TableHead>{t('vendors.revenueCategory', 'finance')}</TableHead>
                                    <TableHead>{t('vendors.status', 'finance')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    // Loading skeleton rows
                                    (Array.from({ length: pagination.limit || 20 }).map((_, i) => (
                                        <TableRow key={`skeleton-${i}`}>
                                            <TableCell>
                                                <Skeleton className="h-4 w-4" />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton className="h-4 w-32" />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton className="h-4 w-32" />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton className="h-4 w-24" />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton className="h-4 w-24" />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton className="h-4 w-48" />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton className="h-4 w-32" />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton className="h-4 w-32" />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton className="h-4 w-24" />
                                            </TableCell>
                                        </TableRow>
                                    )))
                                ) : vendors.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} className="text-center py-8">
                                            <div className="flex flex-col items-center gap-2">
                                                <p className="text-muted-foreground">{t('vendors.noVendors', 'finance')}</p>
                                                <Button
                                                    variant="outline"
                                                    onClick={() => window.location.href = '/'}
                                                >
                                                    {t('vendors.uploadToCreate', 'finance')}
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    vendors.map((vendor) => (
                                        <TableRow
                                            key={vendor.id}
                                            className="cursor-pointer hover:bg-muted/50"
                                            onClick={() => handleRowClick(vendor.id)}
                                        >
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    checked={selectedVendorIds.has(vendor.id)}
                                                    onCheckedChange={(checked) => handleToggleVendorSelection(vendor.id, Boolean(checked))}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <div className="font-medium">
                                                    {vendor.name}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1">
                                                    {(tagsByVendorId[vendor.id] || []).length > 0 ? (
                                                        tagsByVendorId[vendor.id].map((tag) => (
                                                            <TagBadge key={tag.id} tag={tag} size="xs" />
                                                        ))
                                                    ) : (
                                                        <span className="text-xs text-muted-foreground">-</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    {vendor.tax_id || <span className="text-secondary">N/A</span>}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    {vendor.address || <span className="text-secondary">N/A</span>}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    {vendor.email && (
                                                        <div>{vendor.email}</div>
                                                    )}
                                                    {vendor.phone && (
                                                        <div>{vendor.phone}</div>
                                                    )}
                                                    {!vendor.email && !vendor.phone && <span className="text-secondary">N/A</span>}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    {vendor.cost_category?.name || <span className="text-secondary">N/A</span>}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    {vendor.revenue_category?.name || <span className="text-secondary">N/A</span>}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={vendor.is_active ? 'default' : 'secondary'}>
                                                    {vendor.is_active ? t('status.active', 'common') : t('status.inactive', 'common')}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>

                {/* Pagination */}
                <PaginationFooter
                    pagination={pagination}
                    onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                    onPageSizeChange={(pageSize) => setPagination(prev => ({ ...prev, limit: pageSize, page: 1 }))}
                />
            </div>
        </Layout>
    )
}

export const Route = createFileRoute('/partners/')({
    component: VendorsPage,
})
